import { useMemo, useRef, useEffect, useState } from 'react';
import {
  StyledProductPerformanceChart,
  StyledGradientBar,
  StyledTriangle,
  StyleStressTextContainer,
  StyledRow,
  StyledChartContainer,
  StyledProductName,
  DotColor,
  ProductList,
} from './ProductPerformance.styles';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from 'hooks';
import { BarChartLabelProps } from 'components/BarChart/BarChartLabel';
import { getYMax } from 'utils/getYMaxEnvironmentalStress';
import { Product } from 'base/types/RecommendationCropwise';

export interface ProductData {
  CATEGORY: string;
  WIN_PRCT: number;
  CHK_PLS_MNS: number;
  ENT_YLD_AVG: number;
  value: number;
  barColor: string;
  color: string;
  titleColor: string;
  title: string;
  temperatureLabel: string;
  precipitationLabel: string;
}

interface ProductPerformanceChartProps {
  data: Record<string, ProductData[]>;
  environmentalStressData: BarChartLabelProps[];
  yMin?: number;
  recommendedProducts?: Product[];
}

export const ProductPerformanceChart: React.FC<ProductPerformanceChartProps> = ({
  data = {},
  environmentalStressData = [],
  yMin = 0,
  recommendedProducts,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile, landscape, isTablet } = useBreakpoint();
  const [parentHeight, setParentHeight] = useState(330);

  const [t] = useTranslation();

  const distanceBetweenBars = useMemo(() => {
    if (isTablet && !landscape) {
      return 50;
    } else if (isMobile && landscape) {
      return 60;
    } else {
      return 24;
    }
  }, [isTablet, landscape, isMobile]);

  const transformData = useMemo(() => {
    const orderedCategories = environmentalStressData
      .map((item) => item.Category)
      .filter((category): category is string => Boolean(category));
    const fallbackCategories = [
      ...Array.from(
        new Set(Object.values(data).flatMap((arr) => arr.map((item) => item.CATEGORY)))
      ),
    ];
    const categories = orderedCategories.length ? orderedCategories : fallbackCategories;

    return categories.map((category) => {
      const productValues = Object.entries(data).map(([, productData]) => {
        const categoryData = productData.find((item) => item.CATEGORY === category);
        return {
          value: categoryData?.value || 0,
          barColor: categoryData?.barColor || '#0071CD',
          color: categoryData?.color || '#0071CD',
          title: categoryData?.title || '',
          temperatureLabel: categoryData?.temperatureLabel || '',
          precipitationLabel: categoryData?.precipitationLabel || '',
          titleColor: categoryData?.titleColor || '#000000',
        };
      });

      const id = /^[0-9]+$/.test(category) ? parseInt(category, 10) : -1; // Validate category
      return {
        id,
        title: productValues[0]?.title || '',
        temperatureLabel: productValues[0]?.temperatureLabel || '',
        precipitationLabel: productValues[0]?.precipitationLabel || '',
        value: productValues.map((pv) => pv.value),
        barColor: productValues[0]?.barColor || '#0071CD',
        titleColor: productValues[0]?.titleColor || '#000000',
      };
    });
  }, [data, environmentalStressData]);

  const productColors = useMemo(() => {
    const productKeys = Object.keys(data);
    const colors: string[] = [];

    productKeys.forEach((productKey) => {
      const productData = data[productKey];
      if (productData && productData.length > 0) {
        const firstItem = productData[0];
        colors.push(firstItem.color || '#0071CD');
      }
    });

    return colors;
  }, [data]);

  useEffect(() => {
    if (isMobile && landscape) {
      setParentHeight(containerRef.current?.offsetParent?.clientHeight ?? 330);
    }
  }, [containerRef, isMobile, landscape]);

  const chartHeight = isMobile && landscape ? parentHeight : 360;
  const chartPaddingLeft = isTablet ? 70 : undefined;

  function getYMaxValue() {
    const getYmaxPPC = getYMax(transformData);
    const getYmaxES = getYMax(environmentalStressData);
    const maxValue = Math.max(getYmaxPPC, getYmaxES);
    return Math.ceil(maxValue / 20) * 20;
  }

  return (
    <div ref={containerRef}>
      <StyledChartContainer>
        {/* Environmental Stress Chart (Background) */}
        <StyledProductPerformanceChart
          className="environmental-chart"
          yMin={0}
          yMax={getYMaxValue()}
          yStep={10}
          yLabel={`${t('Environment Frequency')} (%)`}
          // yLabelRight={`${t('Environment Frequency')} (%)`}
          data={environmentalStressData}
          paddingLeft={chartPaddingLeft}
          distanceBetweenBars={distanceBetweenBars}
          showTooltip={false}
          height={chartHeight}
          showYaxisToRight={true}
          showLabels={true}
          opacity={0.5}
          showValuePercentage={true}
        />

        {/* Product Performance Chart (Foreground) */}
        <StyledProductPerformanceChart
          className="performance-chart"
          yMin={yMin}
          yMax={getYMaxValue() / 100}
          yStep={0.1}
          data={transformData}
          yLabel={t('Product Performance')}
          yLabelLeft={t('Product Performance')}
          areValuesDecimal
          id={'product-performance-chart'}
          showTooltip={isMobile || isTablet || (isMobile && landscape)}
          height={chartHeight}
          paddingLeft={chartPaddingLeft}
          distanceBetweenBars={distanceBetweenBars}
          isGroupBar={true}
          groupBarsColors={productColors}
          showLabels={false}
          showValuePercentage={false}
          showHoverGuide={true}
        />
      </StyledChartContainer>
      <StyledRow justify="center">
        <StyledGradientBar />
        <StyleStressTextContainer>
          <div>{t('Cold Stress')}</div>
          <div>{t('Moderate Stress')}</div>
          <div>{t('Heat Stress')}</div>
        </StyleStressTextContainer>
      </StyledRow>
      <ProductList>
        {recommendedProducts?.map((prod) => {
          return (
            <StyledProductName key={prod.product_name}>
              <DotColor color={prod.color ?? ''} />
              {prod.commercial_name}
            </StyledProductName>
          );
        })}
      </ProductList>
    </div>
  );
};
